import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AppointmentCreate, Service } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label, Select, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { DateShortcutButtons, localDateTimeInput } from '../../components/ui/time-range';
import { api } from '../../shared/api';
import { labelForServiceCategory } from '../../shared/i18n';

const serviceCategories: Service['category'][] = ['grooming', 'spa', 'transportation'];

function toIsoDateTime(value: string) {
  return new Date(value).toISOString();
}

function dateParamToLocalDate(value: string | null) {
  if (!value) return new Date();
  const date = new Date(`${value}T09:00:00`);
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

export function AppointmentCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialDate = useMemo(() => dateParamToLocalDate(searchParams.get('date')), [searchParams]);
  const initialCustomerId = searchParams.get('customerId') ?? '';
  const initialDogId = searchParams.get('dogId') ?? '';
  const initialCategory = serviceCategories.includes(searchParams.get('category') as Service['category']) ? searchParams.get('category') as Service['category'] : 'grooming';
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [serviceCategory, setServiceCategory] = useState<Service['category']>(initialCategory);
  const [selectedDogIds, setSelectedDogIds] = useState<string[]>(initialDogId ? [initialDogId] : []);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState(() => localDateTimeInput(initialDate));
  const [notes, setNotes] = useState('');

  const dogsQuery = useQuery({ queryKey: ['customer-dogs', customerId, 'appointments'], queryFn: () => api.customers.allDogs(customerId), enabled: Boolean(customerId) });
  const servicesQuery = useQuery({ queryKey: ['services'], queryFn: () => api.services.list({ limit: 100 }) });
  const categoryServices = (servicesQuery.data?.items ?? []).filter((service) => service.category === serviceCategory && service.active !== false);

  const createAppointment = useMutation({
    mutationFn: () => {
      const input: AppointmentCreate = {
        customerId,
        dogIds: selectedDogIds,
        serviceCategory,
        serviceIds: selectedServiceIds,
        scheduledAt: toIsoDateTime(scheduledAt),
        notes: notes || undefined,
      };
      return api.appointments.create(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['appointments'] });
      navigate(serviceCategory === 'transportation' ? '/admin/transportation' : '/admin/grooming');
    },
  });

  const toggleDog = (dogId: string) => setSelectedDogIds((current) => current.includes(dogId) ? current.filter((id) => id !== dogId) : [...current, dogId]);
  const toggleService = (serviceId: string) => setSelectedServiceIds((current) => current.includes(serviceId) ? current.filter((id) => id !== serviceId) : [...current, serviceId]);

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Nueva cita de grooming</h1>
        <p className="mt-1 text-sm text-slate-500">Programa servicios por cliente y uno o mas perros.</p>
      </div>
      <Card>
        <CardHeader><h2 className="text-sm font-semibold">Datos de la cita</h2></CardHeader>
        <CardContent>
          <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); createAppointment.mutate(); }}>
            <div className="grid gap-1.5">
              <Label htmlFor="appointmentCustomer">Cliente</Label>
              <CustomerSearchBox id="appointmentCustomer" value={customerId} placeholder="Selecciona un cliente" onChange={(id) => { setCustomerId(id); setSelectedDogIds([]); }} />
            </div>
            <div className="grid gap-1.5">
              <Label>Perros</Label>
              <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
                {!customerId ? <p className="text-sm text-slate-500">Selecciona un cliente primero.</p> : null}
                {customerId && (dogsQuery.data?.items ?? []).length === 0 ? <p className="text-sm text-slate-500">Este cliente no tiene perros registrados.</p> : null}
                {(dogsQuery.data?.items ?? []).map((dog) => (
                  <label key={dog.id} className="flex items-center gap-2 text-sm text-[#334033]">
                    <input type="checkbox" checked={selectedDogIds.includes(dog.id)} onChange={() => toggleDog(dog.id)} />
                    {dog.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="serviceCategory">Tipo</Label>
                <Select id="serviceCategory" value={serviceCategory} onChange={(event) => { setServiceCategory(event.target.value as Service['category']); setSelectedServiceIds([]); }}>
                  {serviceCategories.map((category) => <option key={category} value={category}>{labelForServiceCategory(category)}</option>)}
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="scheduledAt">Fecha y hora</Label>
                <Input id="scheduledAt" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
                <DateShortcutButtons unit="hours" onToday={() => setScheduledAt(localDateTimeInput(new Date()))} />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Servicios</Label>
              <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
                {categoryServices.length === 0 ? <p className="text-sm text-slate-500">No hay servicios activos de este tipo.</p> : null}
                {categoryServices.map((service) => (
                  <label key={service.id} className="flex items-center gap-2 text-sm text-[#334033]">
                    <input type="checkbox" checked={selectedServiceIds.includes(service.id)} onChange={() => toggleService(service.id)} />
                    {service.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="appointmentNotes">Notas</Label>
              <Textarea id="appointmentNotes" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
            {createAppointment.isError ? <p className="text-sm text-rose-600">{createAppointment.error.message}</p> : null}
            <Button type="submit" disabled={!customerId || selectedDogIds.length === 0 || selectedServiceIds.length === 0 || createAppointment.isPending}>
              <CalendarPlus className="h-4 w-4" aria-hidden="true" />
              Crear cita
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
